import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";

import { Caution } from "@components/user/editor/partials/editorExamDetail/modalSteps/caution/Caution";

import {
  RadioButton,
  WalkThroughWebcam,
  WalkThroughWebcamItem,
  Msg,
  StackedIcon,
} from "@shared/components";
import {
  ApplicantChallengeModel,
  ApplicantChallengeResultModel,
} from "@shared/models";
import {
  ChallengeStyle,
  ChallengeTakenBy,
  ProgrammingLanguage,
} from "@shared/services/enums";

import {
  TimeLimitPanel,
  ProgrammingLanguagePanelNew,
  CodingCheckListPanelNew,
  isShowCLIPreRequirements,
  getCurrentProgrammingLanguage,
  WebcamSetupPanel,
  getCurrentFrontendTemplate,
} from "./partials";
import { FrontendChallengePanel } from "./partials/frontendChallengePanel/FrontendChallengePanel";

/**
 * Prop interface
 */
export interface StartChallengeWalkThroughProps {
  isOpen: boolean;
  challenge: ApplicantChallengeModel;
  challengeResult: ApplicantChallengeResultModel;
  hasVerifiedLiveness: boolean;
  onStart: (payload: {
    frontendTemplate?: string;
    programmingLanguage?: ProgrammingLanguage;
    takenBy?: ChallengeTakenBy;
  }) => void;
  onCancel: () => void;
}

/**
 * State interface
 */
export interface StartChallengeWalkThroughState {
  programmingLanguage?: ProgrammingLanguage;
  frontendTemplate?: string;
  takenBy: ChallengeTakenBy;
  currentIndex: number;
  environmentTabIndex: number;
  codingCheckValid: boolean;
  hasWebcamSetup: boolean; // NOTE: Type of challenge that can use a webcam
  localExamEnabled: boolean;
  webcamSetupValid: boolean;
}

/**
 * Page component
 */
class StartChallengeWalkThrough extends React.Component<
  StartChallengeWalkThroughProps,
  StartChallengeWalkThroughState
> {
  constructor(props: StartChallengeWalkThroughProps) {
    super(props);

    this.state = {
      programmingLanguage: getCurrentProgrammingLanguage(
        props.challenge,
        props.challengeResult,
      ),
      frontendTemplate: getCurrentFrontendTemplate(
        props.challenge,
        props.challengeResult,
      ),
      takenBy: ChallengeTakenBy.WebBrowser,
      currentIndex: 0,
      environmentTabIndex: 0,
      codingCheckValid: false,
      hasWebcamSetup: [
        ChallengeStyle.Algorithm,
        ChallengeStyle.Function,
        ChallengeStyle.Development,
        ChallengeStyle.Quiz,
      ].includes(props.challenge.style),
      webcamSetupValid: false,
      localExamEnabled: props.challenge.localExamEnabled,
    };
  }

  public componentDidUpdate() {
    const { isOpen, challengeResult, challenge } = this.props;
    if (!isOpen) {
      this.setState({
        programmingLanguage: getCurrentProgrammingLanguage(
          challenge,
          challengeResult,
        ),
        frontendTemplate: getCurrentFrontendTemplate(
          challenge,
          challengeResult,
        ),
        takenBy: ChallengeTakenBy.NotSelected,
        currentIndex: 0,
        environmentTabIndex: 0,
        codingCheckValid: false,
        hasWebcamSetup: [
          ChallengeStyle.Algorithm,
          ChallengeStyle.Function,
          ChallengeStyle.Development,
          ChallengeStyle.Quiz,
        ].includes(challenge.style),
      });
    }
  }

  public shouldComponentUpdate(
    nextProps: StartChallengeWalkThroughProps,
    nextState: StartChallengeWalkThroughState,
  ) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public render() {
    const {
      isOpen,
      onCancel,
      onStart,
      challenge,
      challengeResult,
      hasVerifiedLiveness,
    } = this.props;

    const rootStyle = classnames("code-start-challenge-walk-through");

    const hasCli = isShowCLIPreRequirements(challenge);

    const items: Array<WalkThroughWebcamItem> = [];

    // Walkthrough Card Zero - Cautions -----------------------------------------------

    items.push({
      title: <Msg id="start-challenge-walkthrough.cautions.title" />,
      component: (
        <div className="code-c-walk-through__item with-card-padding">
          <div className="code-c-walk-through__caution">
            <Caution />
          </div>
        </div>
      ),
    });

    // Walkthrough Card One - Challenge Setup -----------------------------------------------

    if (ChallengeStyle.isCodingTypeChallenge(challenge?.style)) {
      items.push({
        title: <Msg id="start-challenge-walkthrough.setup.title" />,
        component: (
          <div className="code-c-walk-through__item with-card-padding">
            <div className="code-c-walk-through__item__inner">
              {hasCli && (
                <div className="code-c-walk-through__solving">
                  <div className="code-c-walk-through__item__subtitle">
                    <Msg id="start-challenge-walkthrough.setup.environment" />
                  </div>
                  <Msg id="start-challenge-walkthrough.setup.local.info" />
                  <div className="code-c-radio-tabs">
                    <RadioButton
                      name="environment"
                      outlined
                      checked={this.state.environmentTabIndex === 0}
                      onChange={() =>
                        this.setState({
                          takenBy: ChallengeTakenBy.WebBrowser,
                          environmentTabIndex: 0,
                        })
                      }
                    >
                      <StackedIcon
                        className="code-c-code-radiotab-icon"
                        icons={[
                          { type: "square", variant: "solid", size: "large" },
                          { type: "globe", variant: "solid", size: "large" },
                        ]}
                      />
                      <Msg
                        className="code-c-radiotab-aftericon"
                        id="start-challenge-walkthrough.setup.browser"
                      />
                    </RadioButton>

                    <RadioButton
                      name="environment"
                      outlined
                      checked={this.state.environmentTabIndex === 1}
                      onChange={() =>
                        this.setState({
                          takenBy: ChallengeTakenBy.LocalMachine,
                          environmentTabIndex: 1,
                        })
                      }
                    >
                      <StackedIcon
                        className="code-c-code-radiotab-icon"
                        icons={[
                          { type: "square", variant: "solid", size: "large" },
                          { type: "desktop", variant: "solid", size: "large" },
                        ]}
                      />
                      <Msg
                        className="code-c-radiotab-aftericon"
                        id="start-challenge-walkthrough.setup.local"
                      />
                    </RadioButton>
                  </div>
                </div>
              )}

              {(!hasCli || this.state.environmentTabIndex === 0) && (
                <CodingCheckListPanelNew // TTODO: to be updated once isWebcamEnabled feature flag is not needed
                  takenBy={ChallengeTakenBy.WebBrowser}
                  challenge={challenge}
                  onChange={(codingCheckValid) =>
                    this.setState({ codingCheckValid })
                  }
                />
              )}
              {this.state.environmentTabIndex === 1 && (
                <CodingCheckListPanelNew // TTODO: to be updated once isWebcamEnabled feature flag is not needed
                  takenBy={ChallengeTakenBy.LocalMachine}
                  challenge={challenge}
                  onChange={(codingCheckValid) =>
                    this.setState({ codingCheckValid })
                  }
                />
              )}
              {challenge.isProgrammingChallenge() &&
                (!challengeResult || !challengeResult.programmingLanguage) && (
                  <ProgrammingLanguagePanelNew // TTODO: to be updated once isWebcamEnabled feature flag is not needed
                    initialValue={this.state.programmingLanguage}
                    challenge={challenge}
                    onChange={(programmingLanguage) =>
                      this.setState({ programmingLanguage })
                    }
                  />
                )}
              {challenge.isFrontendChallenge() &&
                !challengeResult?.codingTemplate?.templateCode && (
                  <FrontendChallengePanel
                    value={this.state.frontendTemplate}
                    challenge={challenge}
                    onChange={(frontendTemplate) =>
                      this.setState({ frontendTemplate })
                    }
                  />
                )}
            </div>
          </div>
        ),
        hasPrevButton: true,
        nextButtonDisabled: !this.state.codingCheckValid,
      });
    }

    // Walkthrough Card Three - Webcam -----------------------------------------------

    if (
      this.state.hasWebcamSetup && // Enabled on challenge
      hasVerifiedLiveness // User has done face liveness check
    ) {
      items.push({
        title: <Msg id="start-challenge-walkthrough.setupwebcam.title" />,
        component: (
          <div className="code-c-walk-through__item with-card-padding">
            <WebcamSetupPanel
              onWebcamConnect={this.onWebcamSetupConnect}
              onWebcamDisconnect={this.onWebcamSetupDisconnect}
            />
          </div>
        ),
        nextButtonDisabled: !this.state.webcamSetupValid,
        hasPrevButton: true,
      });
    }

    // Walkthrough Card Four - Time Limit -----------------------------------------------

    items.push({
      title: <Msg id="start-challenge-walkthrough.startchallenge.title" />,
      component: (
        <div className="code-c-walk-through__item">
          <TimeLimitPanel challenge={challenge} isNew />
        </div>
      ),
      hasPrevButton: true,
      prevButtonLabel: (
        <Msg id={"start-challenge-walkthrough.startchallenge.cancel"} />
      ),
      nextButtonLabel: (
        <Msg id={"start-challenge-walkthrough.startchallenge.start"} />
      ),
      onClickNext: () =>
        onStart({
          frontendTemplate: this.state.frontendTemplate,
          programmingLanguage: this.state.programmingLanguage,
          takenBy: this.state.takenBy,
        }),
      onClickPrev: () => onCancel(),
    });

    return (
      <WalkThroughWebcam
        isOpen={isOpen}
        className={rootStyle}
        items={items}
        hasPrevButton={false}
        currentIndex={this.state.currentIndex}
        showSkipButton={false}
        onClose={onCancel}
        onMovePage={this.onMovePage}
      />
    );
  }

  private onWebcamSetupConnect = () => {
    this.setState({
      webcamSetupValid: true,
    });
  };

  private onWebcamSetupDisconnect = () => {
    this.setState({
      webcamSetupValid: false,
    });
  };

  private onMovePage = (currentIndex: number) => {
    this.setState({ currentIndex });
  };
}

export default StartChallengeWalkThrough;
